/* eslint-disable react/no-unescaped-entities */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Linkedin } from "lucide-react";
import { Nav, Footer } from "./Home";
import { HEADSHOT_URL, BRIDGE_STORY, LINKEDIN_URL } from "@/lib/content";

const CREDENTIALS = [
  {
    tag: "Major League Baseball",
    title: "Toronto Blue Jays Baseball Club",
    body: "Marketing for one of the most beloved franchises in North American sport. Learned the discipline of speaking to two audiences at once — the die-hards who show up in April rain, and the once-a-year families who come for the fireworks.",
  },
  {
    tag: "Fortune 100 Technology",
    title: "In-house at a global tech company",
    body: "Built and shipped marketing for a Fortune 100 technology company across web, brand, and product launches. Learned the polish, the process, and the systems that make big brands feel effortless — even when they're anything but.",
  },
  {
    tag: "Family Business · QSR",
    title: "Married into the country's top quick-service restaurant",
    body: "For more than two decades in my career, I lived inside a franchisee company of a major American fast-food chain. Corporate pressure on one side, family budget on the other, hundreds of suppliers in between. This is where I learned what actually keeps small business owners up at night.",
  },
  {
    tag: "Mom & Pop → Franchise",
    title: "Grew a family business into a multi-location powerhouse",
    body: "Helped shape a small family business into a full franchising operation — playbooks, systems, brand, hiring, and every quiet detail that makes a place feel like itself across every location.",
  },
];

const VALUES = [
  { k: "Honesty over upsell", v: "I only recommend what your business actually needs. If a $750 site is right for you, I'll say so — even when the $3,000 one is on the table." },
  { k: "One-time prices", v: "No monthly subscriptions. You pay once, you own the site, and the keys stay in your pocket." },
  { k: "Real design, made by hand", v: "Every layout is drawn by me. No template factory, no drag-and-drop mush. Your business deserves an original." },
  { k: "Slow enough to care", v: "I take on a small number of clients per month so I can actually design your site — not just assemble it." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Your Marketing Partner · Marketing for the Moment";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-cream text-ink">
      <Nav />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-line">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="about-back-home"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.25em] text-ink2 hover:text-rust transition-colors"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="eyebrow">About Your Marketing Partner</p>
              <h1
                data-testid="about-headline"
                className="font-display text-[52px] md:text-[96px] leading-[0.92] tracking-tight text-ink mt-6"
              >
                Andrea Brown.
                <br />
                <span className="italic text-rust">The bridge between</span>
                <br />
                <span className="italic">corporate & kitchen table.</span>
              </h1>
              <p className="mt-8 text-lg md:text-xl leading-relaxed text-ink2 max-w-2xl">
                Twenty-five years of marketing across a Major League Baseball club, a Fortune 100 technology giant,
                and an American quick-service restaurant empire I married into. Now, quietly, I build websites for
                the small businesses I've always most cared about.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="flex flex-col items-center lg:items-end gap-5">
                <div className="relative">
                  <div
                    className="relative w-[220px] h-[220px] md:w-[260px] md:h-[260px] rounded-full overflow-hidden border border-line"
                    style={{ backgroundColor: "#F0EBE1" }}
                    data-testid="about-headshot-frame"
                  >
                    <img
                      src={HEADSHOT_URL}
                      alt="Andrea Brown, Founder"
                      data-testid="about-headshot"
                      className="w-full h-full object-cover"
                      style={{
                        mixBlendMode: "multiply",
                        objectPosition: "center 20%",
                        filter: "blur(0.5px) contrast(0.92) brightness(1.04) saturate(0.88)",
                      }}
                    />
                  </div>
                  <span
                    className="absolute inset-0 rounded-full border border-rust/50 pointer-events-none"
                    style={{ transform: "translate(10px, 10px)" }}
                    aria-hidden="true"
                  />
                </div>

                <div className="text-center lg:text-right">
                  <p className="eyebrow leading-none" style={{ fontSize: "10px" }}>Andrea Brown</p>
                  <p className="font-display italic text-lg text-ink leading-tight mt-1">Founder & Designer</p>
                  <a
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="about-linkedin"
                    className="mt-3 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.2em] text-ink2 hover:text-rust transition-colors"
                  >
                    <Linkedin size={14} /> Connect on LinkedIn
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow">The long story</p>
              <h2 className="font-display italic text-4xl md:text-5xl text-ink mt-4 leading-[1.05]">
                I know your ride home.
              </h2>
              <p className="mt-6 font-display italic text-2xl text-ink2">Because I've lived it too.</p>
            </div>

            <div className="md:col-span-8 space-y-6 text-[17px] leading-[1.85] text-ink2">
              <p>
                I'm a highly skilled marketer with a special affinity for the entrepreneurs, the small family
                businesses, and the sole proprietors for whom the drive home is a hellscape of worry.
              </p>
              <p>
                My parents bought a claims-adjusting business when I was young. Many nights, I'd overhear them at the
                kitchen table — wondering if they'd make payroll, if they'd lose the house, or if they'd be forced to
                let a beloved employee wake up to no career and no way to feed their own family. That fear lives
                quietly in every founder I've ever met.
              </p>
              <p>
                Later, I married into a family business — a franchisee company of a major American fast-food chain.
                For more than two decades I lived inside the vice grip: corporate squeezing one way, the family
                budget the other, hundreds of suppliers in between. I know the fears, the math at 2 a.m., and the
                moments that make you want to give up.
              </p>
              <p>
                I'm here to be a helping hand. To honor the doers — the people who make a community exceptional.
                The ones who turn a town into somewhere you actually love to work, play, and thrive in for a
                lifetime.
              </p>
              <p className="font-display italic text-2xl text-ink leading-snug pt-4">
                Your website should help you breathe a little easier. That's the whole point of mine.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-24 md:py-32 bg-parchment border-y border-line">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="eyebrow">Where I've worked</p>
            <h2 className="font-display text-4xl md:text-6xl text-ink mt-4 leading-[0.95]">
              Boardrooms & <span className="italic">kitchen tables.</span>
            </h2>
            <p className="mt-6 text-ink2 text-lg leading-relaxed">
              Twenty-five years across four very different rooms. Each taught me something small business owners
              rarely get access to — and each shapes every site I make today.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
            {CREDENTIALS.map((c, i) => (
              <article
                key={i}
                data-testid={`credential-${i}`}
                className="bg-cream border border-line p-8 md:p-10 transition-transform hover:-translate-y-1"
              >
                <p className="eyebrow">{c.tag}</p>
                <h3 className="font-display text-2xl md:text-3xl text-ink mt-3 italic leading-tight">{c.title}</h3>
                <p className="text-ink2 mt-4 text-[15px] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>

          <blockquote className="mt-20 max-w-3xl">
            <p className="font-display italic text-3xl md:text-4xl text-ink leading-tight">
              "I've sat in boardrooms and in family kitchens. The good news is — they want exactly the same thing.
              To be seen, to be trusted, to be chosen."
            </p>
            <footer className="mt-6 eyebrow">— Andrea Brown, Founder</footer>
          </blockquote>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="eyebrow">What you can expect from me</p>
            <h2 className="font-display text-4xl md:text-6xl text-ink mt-4 leading-[0.95]">
              A partnership, <span className="italic">not a purchase.</span>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            {VALUES.map((v, i) => (
              <div key={i} className="border-t border-line pt-6" data-testid={`value-${i}`}>
                <p className="font-display italic text-2xl text-ink leading-tight">{v.k}</p>
                <p className="text-ink2 mt-3 text-[15px] leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 md:py-32 bg-ink text-cream text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <p className="eyebrow" style={{ color: "#E29A78" }}>
            {BRIDGE_STORY.signoff}
          </p>
          <h2 className="font-display italic text-4xl md:text-6xl mt-6 leading-[1.05]">
            {BRIDGE_STORY.closing_line_1}
            <br />
            <span style={{ color: "#E29A78" }}>{BRIDGE_STORY.closing_line_2}</span>
          </h2>
          <p className="mt-8 text-cream/75 text-lg leading-relaxed">
            If any of this sounds like the kind of partner you've been quietly hoping to find, I'd love to hear
            about your business. A couple of sentences is plenty — I read every note personally.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-3 bg-rust text-cream px-7 py-4 text-sm uppercase tracking-[0.15em] transition-all hover:bg-cream hover:text-ink"
            >
              Start the conversation <ArrowRight size={16} />
            </a>
            <Link
              to="/#tiers"
              data-testid="about-cta-tiers"
              className="inline-flex items-center gap-3 border border-cream/40 text-cream px-7 py-4 text-sm uppercase tracking-[0.15em] transition-all hover:bg-cream hover:text-ink"
            >
              See the three tiers <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
